<template>
  <div className="ribbon-container"></div>
</template>

<script>
import { onMounted, onUnmounted } from 'vue'

export default {
  name: 'RibbonBackground',
  setup () {
    // 工具函数
    const random = function () {
      if (arguments.length === 1) {
        if (Array.isArray(arguments[0])) {
          const index = Math.round(random(0, arguments[0].length - 1))
          return arguments[0][index]
        }
        return random(0, arguments[0])
      } else if (arguments.length === 2) {
        return Math.random() * (arguments[1] - arguments[0]) + arguments[0]
      }
      return 0
    }

    // 屏幕信息
    const screenInfo = function (e) {
      const width = Math.max(0, window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth || 0)
      const height = Math.max(0, window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight || 0)
      const scrollx = Math.max(0, window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0) - (document.documentElement.clientLeft || 0)
      const scrolly = Math.max(0, window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0) - (document.documentElement.clientTop || 0)

      return {
        width,
        height,
        ratio: width / height,
        centerx: width / 2,
        centery: height / 2,
        scrollx,
        scrolly
      }
    }

    // 点类
    class Point {
      constructor (x, y) {
        this.x = x || 0
        this.y = y || 0
      }

      set (x, y) {
        this.x = x || 0
        this.y = y || 0
      }

      copy (point) {
        this.x = point.x || 0
        this.y = point.y || 0
        return this
      }

      multiply (x, y) {
        this.x *= x || 1
        this.y *= y || 1
        return this
      }

      divide (x, y) {
        this.x /= x || 1
        this.y /= y || 1
        return this
      }

      add (x, y) {
        this.x += x || 0
        this.y += y || 0
        return this
      }

      subtract (x, y) {
        this.x -= x || 0
        this.y -= y || 0
        return this
      }

      clampX (min, max) {
        this.x = Math.max(min, Math.min(this.x, max))
        return this
      }

      clampY (min, max) {
        this.y = Math.max(min, Math.min(this.y, max))
        return this
      }

      flipX () {
        this.x *= -1
        return this
      }

      flipY () {
        this.y *= -1
        return this
      }
    }

    // 丝带工厂
    class RibbonFactory {
      constructor (options) {
        this._canvas = null
        this._context = null
        this._sto = null
        this._width = 0
        this._height = 0
        this._scroll = 0
        this._ribbons = []
        this._options = {
          colorSaturation: '100%',
          colorBrightness: '60%',
          colorAlpha: 0.80,
          colorCycleSpeed: 8,
          verticalPosition: 'center',
          horizontalSpeed: 200,
          ribbonCount: 1,   //丝带数量
          strokeSize: 0,
          parallaxAmount: -0.0,
          animateSections: true
        }

        this.setOptions(options)
        this.init()
      }

      setOptions (options) {
        if (typeof options === 'object') {
          for (const key in options) {
            if (Object.prototype.hasOwnProperty.call(options, key)) {
              this._options[key] = options[key]
            }
          }
        }
      }

      init () {
        try {
          this._canvas = document.createElement('canvas')
          this._canvas.style.cssText = `
            display: block;
            position: fixed;
            margin: 0;
            padding: 0;
            border: 0;
            outline: 0;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
          `
          this._canvas.id = 'bg_canvas'

          this._onResize()
          this._context = this._canvas.getContext('2d')
          this._context.clearRect(0, 0, this._width, this._height)
          this._context.globalAlpha = this._options.colorAlpha

          window.addEventListener('resize', this._onResize.bind(this))
          window.addEventListener('scroll', this._onScroll.bind(this))

          document.body.appendChild(this._canvas)
          this._onDraw()
        } catch (e) {
          console.warn('Canvas Context Error: ' + e.toString())
        }
      }

      addRibbon () {
        const dir = Math.round(random(1, 9)) > 5 ? 'right' : 'left'
        const stop = 1000
        const hide = 200
        const min = 0 - hide
        const max = this._width + hide
        const startx = dir === 'right' ? min : max
        let starty = Math.round(random(0, this._height))

        if (/^(top|min)$/i.test(this._options.verticalPosition)) {
          starty = 0 + hide
        } else if (/^(middle|center)$/i.test(this._options.verticalPosition)) {
          starty = this._height / 2
        } else if (/^(bottom|max)$/i.test(this._options.verticalPosition)) {
          starty = this._height - hide
        }

        const ribbon = []
        const point1 = new Point(startx, starty)
        const point2 = new Point(startx, starty)
        let point3 = null
        let color = Math.round(random(0, 360))
        let delay = 0
        let remainingStop = stop // 用于替代原来的stop变量

        while (true) {
          if (remainingStop <= 0) break
          remainingStop--

          const movex = Math.round((Math.random() * 1 - 0.2) * this._options.horizontalSpeed)
          const movey = Math.round((Math.random() * 1 - 0.5) * (this._height * 0.25))

          point3 = new Point()
          point3.copy(point2)

          if (dir === 'right') {
            point3.add(movex, movey)
            if (point2.x >= max) break
          } else if (dir === 'left') {
            point3.subtract(movex, movey)
            if (point2.x <= min) break
          }

          ribbon.push({
            point1: new Point(point1.x, point1.y),
            point2: new Point(point2.x, point2.y),
            point3,
            color,
            delay,
            dir,
            alpha: 0,
            phase: 0
          })

          point1.copy(point2)
          point2.copy(point3)
          delay += 4
          color += this._options.colorCycleSpeed
        }

        this._ribbons.push(ribbon)
      }

      _drawRibbonSection (section) {
        if (!section) return true

        if (section.phase >= 1 && section.alpha <= 0) {
          return true
        }

        if (section.delay <= 0) {
          section.phase += 0.02
          section.alpha = Math.sin(section.phase) * 1
          section.alpha = section.alpha <= 0 ? 0 : section.alpha
          section.alpha = section.alpha >= 1 ? 1 : section.alpha

          if (this._options.animateSections) {
            const mod = Math.sin(1 + section.phase * Math.PI / 2) * 0.1

            if (section.dir === 'right') {
              section.point1.add(mod, 0)
              section.point2.add(mod, 0)
              section.point3.add(mod, 0)
            } else {
              section.point1.subtract(mod, 0)
              section.point2.subtract(mod, 0)
              section.point3.subtract(mod, 0)
            }

            section.point1.add(0, mod)
            section.point2.add(0, mod)
            section.point3.add(0, mod)
          }
        } else {
          section.delay -= 0.5
        }

        const s = this._options.colorSaturation
        const l = this._options.colorBrightness
        const c = `hsla(${section.color}, ${s}, ${l}, ${section.alpha})`

        this._context.save()

        if (this._options.parallaxAmount !== 0) {
          this._context.translate(0, this._scroll * this._options.parallaxAmount)
        }

        this._context.beginPath()
        this._context.moveTo(section.point1.x, section.point1.y)
        this._context.lineTo(section.point2.x, section.point2.y)
        this._context.lineTo(section.point3.x, section.point3.y)
        this._context.fillStyle = c
        this._context.fill()

        if (this._options.strokeSize > 0) {
          this._context.lineWidth = this._options.strokeSize
          this._context.strokeStyle = c
          this._context.lineCap = 'round'
          this._context.stroke()
        }

        this._context.restore()
        return false
      }

      _onDraw () {
        // 清理无效的ribbon
        this._ribbons = this._ribbons.filter(ribbon => ribbon !== null)

        this._context.clearRect(0, 0, this._width, this._height)

        for (let a = 0; a < this._ribbons.length; ++a) {
          const ribbon = this._ribbons[a]
          if (!ribbon) continue

          const numDone = ribbon.filter(section =>
            this._drawRibbonSection(section)
          ).length

          if (numDone >= ribbon.length) {
            this._ribbons[a] = null
          }
        }

        if (this._ribbons.filter(Boolean).length < this._options.ribbonCount) {
          this.addRibbon()
        }

        requestAnimationFrame(this._onDraw.bind(this))
      }

      _onResize (e) {
        const screen = screenInfo(e)
        this._width = screen.width
        this._height = screen.height

        if (this._canvas) {
          this._canvas.width = this._width
          this._canvas.height = this._height

          if (this._context) {
            this._context.globalAlpha = this._options.colorAlpha
          }
        }
      }

      _onScroll (e) {
        const screen = screenInfo(e)
        this._scroll = screen.scrolly
      }
    }

    let ribbonInstance = null

    onMounted(() => {
      ribbonInstance = new RibbonFactory()
    })

    onUnmounted(() => {
      if (ribbonInstance && ribbonInstance._canvas) {
        document.body.removeChild(ribbonInstance._canvas)
        window.removeEventListener('resize', ribbonInstance._onResize)
        window.removeEventListener('scroll', ribbonInstance._onScroll)
        ribbonInstance = null
      }
    })

    return {}
  }
}
</script>

<style scoped>
.ribbon-container {
  display: none;
  position: fixed;
  z-index: -1; /* 负值确保在底层 */
  pointer-events: none; /* 允许点击穿透 */
}
</style>
